import { action, makeObservable, observable, runInAction } from 'mobx';

import {
  TransportLayer,
  TemplateDto,
  TemplateDefaultSlot,
} from '../../../api/transportLayer';
import { BaseStore } from '../../../services/baseStore';
import { RootStore } from '../../../services/rootStore';

export class TemplatesStore extends BaseStore<{}> {
  rootStore: RootStore;
  transportLayer: TransportLayer;

  templates: TemplateDto[] = [];
  guidedDoingTemplates: TemplateDto[] = [];
  loading: boolean = false;
  error: string | null = null;

  constructor(_rootStore: RootStore) {
    super();
    this.rootStore = _rootStore;
    this.transportLayer = _rootStore.transportLayer;
    makeObservable(this, {
      templates: observable,
      guidedDoingTemplates: observable,
      loading: observable,
      error: observable,
      fetchTemplates: action,
      fetchGuidedDoingTemplates: action,
      setDefaultTemplate: action,
      updateTemplate: action,
      createTemplate: action,
      createCanvasTemplate: action,
      updateCanvasTemplate: action,
      clearTemplates: action,
    });
  }

  clearTemplates() {
    this.templates = [];
    this.error = null;
  }

  public async fetchTemplates() {
    this.loading = true;
    this.error = null;

    try {
      const resp = await this.transportLayer.fetchTemplates();

      if (resp.succeeded) {
        runInAction(() => {
          this.templates = resp.data || [];
          this.loading = false;
        });
      } else {
        runInAction(() => {
          this.error = resp.message || 'Failed to fetch templates';
          this.loading = false;
        });
      }
      return resp;
    } catch (error) {
      runInAction(() => {
        this.error = 'An error occurred while fetching templates';
        this.loading = false;
      });
      return {
        data: [],
        message: 'An error occurred while fetching templates',
        succeeded: false,
        failed: true,
      };
    }
  }

  public async fetchGuidedDoingTemplates() {
    this.loading = true;
    this.error = null;

    try {
      const resp = await this.transportLayer.fetchGuidedDoingTemplates();

      if (resp.succeeded) {
        runInAction(() => {
          this.guidedDoingTemplates = resp.data || [];
          this.loading = false;
        });
      } else {
        runInAction(() => {
          this.error = resp.message || 'Failed to fetch guided-doing templates';
          this.loading = false;
        });
      }
      return resp;
    } catch (error) {
      runInAction(() => {
        this.error = 'An error occurred while fetching guided-doing templates';
        this.loading = false;
      });
      return {
        data: [],
        message: 'An error occurred while fetching guided-doing templates',
        succeeded: false,
        failed: true,
      };
    }
  }


  public async setDefaultTemplate(
    templateId: string,
    slot: TemplateDefaultSlot
  ) {
    this.loading = true;
    this.error = null;

    try {
      const resp = await this.transportLayer.setDefaultTemplate(
        templateId,
        slot
      );

      if (resp.succeeded) {
        await this.fetchTemplates();
      } else {
        runInAction(() => {
          this.error = resp.message || 'Failed to set default template';
          this.loading = false;
        });
      }
      return resp;
    } catch (error) {
      runInAction(() => {
        this.error = 'An error occurred while setting default template';
        this.loading = false;
      });
      return {
        data: null,
        message: 'An error occurred while setting default template',
        succeeded: false,
        failed: true,
      };
    }
  }

  public async updateTemplate(templateId: string, title: string, elements: any[]) {
    this.loading = true;
    this.error = null;

    try {
      const resp = await this.transportLayer.updateTemplate(templateId, title, elements);

      if (resp.succeeded) {
        runInAction(() => {
          // Update the template in the local list
          this.templates = this.templates.map(template =>
            template.id === templateId
              ? { ...template, title, elements }
              : template
          );
          this.loading = false;
        });
      } else {
        runInAction(() => {
          this.error = resp.message || 'Failed to update template';
          this.loading = false;
        });
      }
      return resp;
    } catch (error) {
      runInAction(() => {
        this.error = 'An error occurred while updating template';
        this.loading = false;
      });
      return {
        data: null,
        message: 'An error occurred while updating template',
        succeeded: false,
        failed: true,
      };
    }
  }

  public async createTemplate(sourceTemplateId: string, title: string, elements: any[]) {
    this.loading = true;
    this.error = null;

    try {
      const resp = await this.transportLayer.createTemplate(sourceTemplateId, title, elements);

      if (resp.succeeded) {
        await this.fetchTemplates();
        runInAction(() => {
          this.loading = false;
        });
      } else {
        runInAction(() => {
          this.error = resp.message || 'Failed to create template';
          this.loading = false;
        });
      }
      return resp;
    } catch (error) {
      runInAction(() => {
        this.error = 'An error occurred while creating template';
        this.loading = false;
      });
      return {
        data: null,
        message: 'An error occurred while creating template',
        succeeded: false,
        failed: true,
      };
    }
  }

  public async createCanvasTemplate(title: string, objects: any[], previewUrl?: string) {
    this.loading = true;
    this.error = null;

    try {
      const resp = await this.transportLayer.createCanvasTemplate(title, objects, previewUrl);

      if (resp.succeeded) {
        await this.fetchTemplates(); // Ensure it's in the list
        runInAction(() => {
          this.loading = false;
        });
      } else {
        runInAction(() => {
          this.error = resp.message || 'Failed to create canvas template';
          this.loading = false;
        });
      }
      return resp;
    } catch (error) {
      runInAction(() => {
        this.error = 'An error occurred while creating canvas template';
        this.loading = false;
      });
      return {
        data: null,
        message: 'An error occurred while creating canvas template',
        succeeded: false,
        failed: true,
      };
    }
  }

  public async updateCanvasTemplate(templateId: string, title: string, objects: any[], previewUrl?: string) {
    this.loading = true;
    this.error = null;

    try {
      const resp = await this.transportLayer.updateCanvasTemplate(templateId, title, objects, previewUrl);

      if (resp.succeeded) {
        runInAction(() => {
          // Update the template in the local list
          this.templates = this.templates.map(template =>
            template.id === templateId
              ? {
                ...template,
                title,
                objects,
                previewUrl: previewUrl && previewUrl.length > 0 ? previewUrl : template.previewUrl
              }
              : template
          );
          this.loading = false;
        });
      } else {
        runInAction(() => {
          this.error = resp.message || 'Failed to update canvas template';
          this.loading = false;
        });
      }
      return resp;
    } catch (error) {
      runInAction(() => {
        this.error = 'An error occurred while updating canvas template';
        this.loading = false;
      });
      return {
        data: null,
        message: 'An error occurred while updating canvas template',
        succeeded: false,
        failed: true,
      };
    }
  }
}
